export type CollaborationOperation =
  | {
      kind: "set";
      path: string;
      value: unknown;
    }
  | {
      kind: "unset";
      path: string;
    }
  | {
      kind: "insert";
      path: string;
      index: number;
      value: unknown;
    }
  | {
      kind: "remove";
      path: string;
      index: number;
    };

export interface CollaborationOperationEntry {
  version: number;
  actorId: string;
  ops: CollaborationOperation[];
  timestamp: number;
}

export interface CollaboratorPresence {
  userId: string;
  displayName?: string;
  color?: string;
  cursor?: {
    sectionId?: string;
    fieldId?: string;
    index?: number;
  };
  selection?: {
    startFieldId?: string;
    endFieldId?: string;
  };
  lastActive: number;
}

export interface CollaborationDocumentState<TDocument = unknown> {
  version: number;
  document: TDocument;
  history: CollaborationOperationEntry[];
  presence: Record<string, CollaboratorPresence>;
  lockVersion: number;
  updatedAt: number;
}

export type CollaborationInboundMessage =
  | {
      type: "hello";
      clientId?: string;
      userId: string;
      displayName?: string;
      color?: string;
      clientVersion?: number;
    }
  | {
      type: "snapshot";
      baseVersion: number;
      document: unknown;
      clientSeq?: number;
    }
  | {
      type: "patch";
      clientSeq: number;
      baseVersion: number;
      ops: CollaborationOperation[];
    }
  | {
      type: "presence";
      presence: Partial<CollaboratorPresence>;
    }
  | {
      type: "ping";
      nonce?: string;
    }
  | {
      type: "request_snapshot";
    };

export type CollaborationOutboundMessage =
  | {
      type: "welcome";
      clientId: string;
      version: number;
      document: unknown;
      presence: Record<string, CollaboratorPresence>;
      serverTime: number;
    }
  | {
      type: "ack";
      clientSeq: number;
      version: number;
    }
  | {
      type: "op";
      actorId: string;
      version: number;
      ops: CollaborationOperation[];
      timestamp: number;
    }
  | {
      type: "presence";
      clientId: string;
      presence: CollaboratorPresence | null;
    }
  | {
      type: "resync";
      version: number;
      document: unknown;
      presence: Record<string, CollaboratorPresence>;
    }
  | {
      type: "pong";
      nonce?: string;
      serverTime: number;
    }
  | {
      type: "error";
      code: string;
      message: string;
    };

export function applyCollaborationOperations<TDocument = unknown>(
  document: TDocument,
  operations: CollaborationOperation[]
): TDocument {
  let result = structuredClone(document);
  for (const operation of operations) {
    result = applyOperation(result, operation);
  }
  return result;
}

function applyOperation<TDocument>(document: TDocument, operation: CollaborationOperation): TDocument {
  const root = structuredClone(document);
  switch (operation.kind) {
    case "set":
      setValueAtPath(root, operation.path, operation.value);
      return root;
    case "unset":
      unsetValueAtPath(root, operation.path);
      return root;
    case "insert":
      insertValueAtPath(root, operation.path, operation.index, operation.value);
      return root;
    case "remove":
      removeValueAtPath(root, operation.path, operation.index);
      return root;
    default:
      return root;
  }
}

function parsePointer(path: string): Array<string | number> {
  if (!path) {
    return [];
  }
  const trimmed = path.startsWith("/") ? path.slice(1) : path;
  if (!trimmed) {
    return [];
  }
  return trimmed.split("/").map((segment) => {
    const index = Number(segment);
    return Number.isInteger(index) && String(index) === segment ? index : segment.replace(/~1/g, "/").replace(/~0/g, "~");
  });
}

function setValueAtPath(target: unknown, path: string, value: unknown): void {
  const segments = parsePointer(path);
  if (segments.length === 0) {
    throw new Error("Cannot set value at root path.");
  }
  const key = segments.pop()!;
  const parent = ensurePath(target, segments);
  if (Array.isArray(parent) && typeof key === "number") {
    parent[key] = value;
    return;
  }
  if (typeof parent === "object" && parent !== null) {
    (parent as Record<string, unknown>)[String(key)] = value;
    return;
  }
  throw new Error(`Invalid path: ${path}`);
}

function unsetValueAtPath(target: unknown, path: string): void {
  const segments = parsePointer(path);
  if (segments.length === 0) {
    throw new Error("Cannot unset root path.");
  }
  const key = segments.pop()!;
  const parent = ensurePath(target, segments, false);
  if (!parent) {
    return;
  }
  if (Array.isArray(parent) && typeof key === "number") {
    parent.splice(key, 1);
    return;
  }
  if (typeof parent === "object" && parent !== null) {
    delete (parent as Record<string, unknown>)[String(key)];
  }
}

function insertValueAtPath(target: unknown, path: string, index: number, value: unknown): void {
  const arrayTarget = getAtPath(target, path);
  if (!Array.isArray(arrayTarget)) {
    throw new Error(`Cannot insert into non-array path: ${path}`);
  }
  const insertIndex = clamp(index, 0, arrayTarget.length);
  arrayTarget.splice(insertIndex, 0, value);
}

function removeValueAtPath(target: unknown, path: string, index: number): void {
  const arrayTarget = getAtPath(target, path);
  if (!Array.isArray(arrayTarget)) {
    throw new Error(`Cannot remove from non-array path: ${path}`);
  }
  if (index < 0 || index >= arrayTarget.length) {
    return;
  }
  arrayTarget.splice(index, 1);
}

function ensurePath(target: unknown, segments: Array<string | number>, createIntermediate = true): any {
  let current: any = target;
  for (const segment of segments) {
    if (segment === undefined || segment === null) {
      throw new Error("Invalid path segment.");
    }
    if (Array.isArray(current)) {
      const numericSegment = typeof segment === "number" ? segment : Number(segment);
      if (!Number.isInteger(numericSegment)) {
        throw new Error("Expected numeric index for array path.");
      }
      if (numericSegment < 0 || numericSegment > current.length) {
        if (!createIntermediate) {
          return null;
        }
        current[numericSegment] = {};
      }
      if (current[numericSegment] === undefined && createIntermediate) {
        current[numericSegment] = {};
      }
      current = current[numericSegment];
      continue;
    }
    if (typeof current !== "object" || current === null) {
      if (!createIntermediate) {
        return null;
      }
      current = {};
    }
    const key = String(segment);
    if (!(key in current) && createIntermediate) {
      current[key] = {};
    }
    current = current[key];
  }
  return current;
}

function getAtPath(target: unknown, path: string): any {
  const segments = parsePointer(path);
  let current: any = target;
  for (const segment of segments) {
    if (current === undefined || current === null) {
      return undefined;
    }
    if (Array.isArray(current)) {
      const index = typeof segment === "number" ? segment : Number(segment);
      if (!Number.isInteger(index) || index < 0 || index >= current.length) {
        return undefined;
      }
      current = current[index];
      continue;
    }
    if (typeof current !== "object") {
      return undefined;
    }
    current = (current as Record<string, unknown>)[String(segment)];
  }
  return current;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
